import {
  existingFromDuplicateConflict,
  leftoverModelsToDelete,
  pickPlacementSource,
  resolveExistingIfcModel,
} from "./ifc-replace-placement";

async function deleteQuietly(deleteIfcModel, projectId, id) {
  if (!id) return;
  try {
    await deleteIfcModel(projectId, id);
  } catch (_) {
    /* already gone via API replace_existing */
  }
}

async function deleteLeftovers(deleteIfcModel, projectId, previousModels, keepId) {
  const leftovers = leftoverModelsToDelete(previousModels, keepId);
  for (const row of leftovers) {
    await deleteQuietly(deleteIfcModel, projectId, row.id);
  }
}

/**
 * Create an IFC model, or replace every row on the same house+discipline.
 *
 * Prefers API `replace_existing` (destroy one duplicate + create). Always
 * deletes leftover local matches afterwards so a second RIV cannot stay in
 * the list when uniqueness is not enforced. If replace_existing fails
 * (422 uniqueness with two rows), deletes the old rows and creates again.
 */
export async function createOrReplaceIfcModel({
  projectId,
  payload,
  existingModels = [],
  replaceConfirmed = false,
  createIfcModel,
  deleteIfcModel,
}) {
  const existing = existingModels || [];
  const previous = pickPlacementSource(existing);

  if (replaceConfirmed && existing.length > 0) {
    try {
      const row = await createIfcModel(projectId, payload, {
        replaceExisting: true,
      });
      await deleteLeftovers(deleteIfcModel, projectId, existing, row?.id);
      return { row, replaced: true, previous };
    } catch (_) {
      for (const prev of existing) {
        await deleteQuietly(deleteIfcModel, projectId, prev.id);
      }
      const row = await createIfcModel(projectId, payload);
      return { row, replaced: true, previous };
    }
  }

  try {
    const row = await createIfcModel(projectId, payload);
    return { row, replaced: false, previous: null };
  } catch (err) {
    const conflict = resolveExistingIfcModel(
      previous,
      existingFromDuplicateConflict(err),
    );
    if (err?.response?.status === 409 && conflict) {
      const row = await createIfcModel(projectId, payload, {
        replaceExisting: true,
      });
      const toClear = existing.length ? existing : [conflict];
      await deleteLeftovers(deleteIfcModel, projectId, toClear, row?.id);
      if (
        conflict.id &&
        String(conflict.id) !== String(row?.id) &&
        !toClear.some((model) => String(model.id) === String(conflict.id))
      ) {
        await deleteQuietly(deleteIfcModel, projectId, conflict.id);
      }
      return { row, replaced: true, previous: pickPlacementSource([conflict, ...existing]) };
    }
    throw err;
  }
}
